// Deterministic world-state fingerprint (NET-1/2). The isomorphism proof: the same
// pure sim, run headless on the Node server and in the browser replica, must reach
// byte-identical block + light state after N ticks of the same seed. `hashWorld`
// canonically folds that state into a 64-bit hex digest so the two can be compared
// with a single string equality (server-vs-solo, and the 10k-tick seed-42 test).
//
// Canonical traversal (independent of Map insertion / chunk-load order): loaded
// chunks sorted by (cx, cz), sections 0..23, cells 0..4095. Per cell the RESOLVED
// global state id is folded (palette ordering is irrelevant), then packed sky/block
// light. Time, day-time, and weather round out the digest. Pure: no DOM, no three,
// Number math only (no BigInt) so it is cheap enough to fold a whole region.
import { WORLD } from '../config';
import type { World } from './world';

const SECTION_VOLUME = WORLD.SECTION * WORLD.SECTION * WORLD.SECTION;

/** Two independent 32-bit FNV-1a lanes (different primes + rotations) combined into
 *  a 64-bit digest — collision-safe enough that an inequality means a real state
 *  divergence, not a hash clash. */
class Digest {
  private a = 0x811c9dc5 | 0; // FNV offset basis
  private b = 0x1b873593 | 0; // murmur c1 as a distinct basis

  fold(word: number): void {
    let a = this.a, b = this.b;
    a ^= word | 0;
    a = Math.imul(a, 0x01000193);
    a = (a << 13) | (a >>> 19);
    b ^= (word + 0x9e3779b9) | 0;
    b = Math.imul(b, 0x85ebca6b);
    b = (b << 15) | (b >>> 17);
    this.a = a;
    this.b = b;
  }

  hex(): string {
    // Extra avalanche on each lane so low-entropy tails still diffuse.
    let a = this.a, b = this.b;
    a ^= a >>> 16; a = Math.imul(a, 0x7feb352d); a ^= a >>> 15;
    b ^= b >>> 13; b = Math.imul(b, 0xc2b2ae35); b ^= b >>> 16;
    const hi = (a >>> 0).toString(16).padStart(8, '0');
    const lo = (b >>> 0).toString(16).padStart(8, '0');
    return hi + lo;
  }
}

export interface HashOptions {
  /** Fold section light (sky/block) as well as block state (default true). Both are
   *  pure sim output; excluding light isolates the block simulation when debugging. */
  light?: boolean;
}

/** 64-bit hex fingerprint of the simulated world state (blocks + light + time +
 *  weather). Deterministic across environments for the same seed and tick history. */
export function hashWorld(world: World, opts: HashOptions = {}): string {
  const withLight = opts.light !== false;
  const d = new Digest();
  d.fold(world.tickCount);
  d.fold(world.dayTime);
  const w = world.weather;
  d.fold((w.raining ? 1 : 0) | (w.thundering ? 2 : 0));
  d.fold(w.rainCountdown);
  d.fold(w.thunderCountdown);

  // Canonical chunk order — sorted, so load order can't perturb the digest.
  const chunks = [...world.loadedChunks()].sort((p, q) => (p.cx - q.cx) || (p.cz - q.cz));
  d.fold(chunks.length);
  for (const chunk of chunks) {
    d.fold(chunk.cx);
    d.fold(chunk.cz);
    for (let si = 0; si < WORLD.SECTIONS_PER_COLUMN; si++) {
      const sec = chunk.sections[si];
      // A section holding no blocks is equivalent to a null section for the BLOCK
      // view: lighting may allocate an all-air section just to store light, and that
      // allocation is not part of the persisted/block state (only the null-vs-object
      // distinction would otherwise leak). When folding light we keep such a section
      // (its light is meaningful, and identical sim runs allocate it identically).
      if (!sec || (sec.nonAirCount === 0 && !withLight)) { d.fold(0xffff); continue; }
      d.fold(si);
      d.fold(sec.nonAirCount);
      for (let li = 0; li < SECTION_VOLUME; li++) d.fold(sec.getIndex(li));
      if (withLight) {
        for (let li = 0; li < SECTION_VOLUME; li++) {
          d.fold((sec.skyLight[li]! << 4) | sec.blockLight[li]!);
        }
      }
    }
  }
  return d.hex();
}
